import { useEffect, useMemo, useState, createContext, useContext } from "react";

const THEME_KEY = "sk-theme";

const ThemeCtx = createContext({ theme: "light", toggle: () => {}, setTheme: () => {} });

// Applied inline in index.html (via public/theme-init.js) to avoid FOUC.
// This provider then keeps the React state in sync.

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "light";
    // Prefer whatever the pre-mount inline script already set on <html>.
    if (document.documentElement.classList.contains("dark")) return "dark";
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    const html = document.documentElement;
    if (theme === "dark") html.classList.add("dark");
    else html.classList.remove("dark");
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch (_) {
      /* private mode / storage disabled — no-op */
    }
    // Keep <meta name="theme-color"> in sync for mobile browsers
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#14100D" : "#2C4A3B");
  }, [theme]);

  const value = useMemo(() => {
    const toggle = () => setThemeState((t) => (t === "dark" ? "light" : "dark"));
    const setTheme = (v) => setThemeState(v === "dark" ? "dark" : "light");
    return { theme, toggle, setTheme };
  }, [theme]);

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
